// src/pages/Home.jsx
/* ----------------------------------------------------------------------------
   شاشة رئيسية للتنقّل – تُظهر بطاقات الأدمن إذا كان role=1 + زر تسجيل خروج
   ✅ تصميم جديد بألوان الصورة فقط (Grayscale)
   ✅ نقل تهيئة الطابعة إلى useEffect (بدون تكرار كل Render)
---------------------------------------------------------------------------- */
import { Card, CardBody, Typography } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import {
  HomeIcon,
  PlusCircleIcon,
  PresentationChartBarIcon,
  DocumentTextIcon,
  DocumentPlusIcon,
  SquaresPlusIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UserPlusIcon,
  ArrowLeftOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { signOut, onAuthStateChanged } from "firebase/auth";

// ✅ عدّل المسار إذا كان firebaseConfig عندك بمكان مختلف
import { auth } from "../../../firebaseConfig";

/* ألوان مستخرجة من الصورة (Grayscale فقط) */
const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

const BASE_CARDS = [
  { title: "إضافة فاتورة", path: "/add_invoice" },
  { title: "الفواتير", path: "/invoice" },
  { title: "الصندوق", path: "/cashbox" },
  { title: "الإعدادات", path: "/printer-test" },
];

const ICONS = {
  "/": HomeIcon,
  "/add_invoice": DocumentPlusIcon,
  "/add_item": PlusCircleIcon,
  "/item_reports": PresentationChartBarIcon,
  "/invoice": DocumentTextIcon,
  "/add_category": SquaresPlusIcon,
  "/settings": Cog6ToothIcon,
  "/printer-test": Cog6ToothIcon,
  "/users_admin": UserGroupIcon,
  "/create_user": UserPlusIcon,
};

function HeaderSketch() {
  return (
    <svg viewBox="0 0 1200 220" className="absolute inset-0 w-full h-full">
      {/* خلفية خطوط خفيفة مثل قلم */}
      {Array.from({ length: 26 }).map((_, i) => (
        <line
          key={i}
          x1={0}
          y1={20 + i * 8}
          x2={1200}
          y2={15 + i * 8}
          stroke="rgba(255,255,255,0.10)"
          strokeWidth="2"
        />
      ))}
      {/* شكل سحابي بسيط */}
      <path
        d="M160 120c70-70 180-55 225 20 35-35 105-42 146 0 60-32 152-10 178 52 92-42 210-12 250 65"
        fill="none"
        stroke="rgba(255,255,255,0.16)"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [role, setRole] = useState(Number(localStorage.getItem("role") || "2"));

  const isAdmin = role === 1;

  // ✅ تهيئة أدوات الطباعة مرة واحدة فقط
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const api = await import("tauri-plugin-printer-v2");
        const { getPrinters, printHtml } = api;

        const extractNames = (raw) => {
          let arr = raw;
          if (typeof raw === "string") {
            try {
              arr = JSON.parse(raw);
            } catch {
              arr = [raw];
            }
          }
          if (arr && Array.isArray(arr.printers)) arr = arr.printers;
          if (!Array.isArray(arr)) arr = [];

          const toName = (item) => {
            if (typeof item === "string") return item;
            if (item && typeof item === "object") {
              if (typeof item.Name === "string" && item.Name.trim())
                return item.Name.trim();
              if (typeof item.DisplayName === "string" && item.DisplayName.trim())
                return item.DisplayName.trim();
              if (typeof item.PrinterName === "string" && item.PrinterName.trim())
                return item.PrinterName.trim();

              const known =
                item.name ||
                item.display_name ||
                item.displayName ||
                item.printerName ||
                item.deviceName ||
                item.label ||
                item.id;

              if (typeof known === "string" && known.trim()) return known.trim();

              for (const [k, v] of Object.entries(item)) {
                if (/name|printer/i.test(k) && typeof v === "string" && v.trim())
                  return v.trim();
              }

              const firstString = Object.values(item).find(
                (v) => typeof v === "string" && v.trim()
              );
              if (firstString) return String(firstString).trim();
            }
            return "";
          };

          const names = arr
            .map(toName)
            .map(String)
            .map((s) => s.trim())
            .filter(Boolean);

          return Array.from(new Set(names));
        };

        // expose helpers
        window.listPrinters = async () => {
          const raw = await getPrinters();
          let arr = raw;

          if (typeof raw === "string") {
            try {
              arr = JSON.parse(raw);
            } catch {}
          }
          if (arr && Array.isArray(arr.printers)) arr = arr.printers;
          if (!Array.isArray(arr)) arr = [];

          console.table(
            arr.map((p) => {
              if (typeof p === "string")
                return { Name: p, DriverName: "", PortName: "", Status: "" };
              return {
                Name: p.Name ?? p.name ?? "",
                DriverName: p.DriverName ?? p.driverName ?? "",
                PortName: p.PortName ?? p.portName ?? "",
                Status: p.PrinterStatus ?? p.status ?? "",
              };
            })
          );

          return extractNames(raw);
        };

        window.savePrinters = (p1, p2) => {
          localStorage.setItem("printer_1", p1 || "");
          localStorage.setItem("printer_2", p2 || "");
          localStorage.setItem("print_method", "plugin");
          console.log("Saved printer names:", { p1, p2 });
        };

        window.testPrint = async () => {
          const p1 = localStorage.getItem("printer_1");
          const p2 = localStorage.getItem("printer_2");
          const html = `<!doctype html>
            <html><body style="width:80mm;padding:8px;font-family:Tahoma">
              <h3 style="margin:0 0 8px 0;">TEST PRINT</h3>
              <div>${new Date().toLocaleString("ar-IQ")}</div>
            </body></html>`;

          const job = (id, printer) =>
            printer
              ? printHtml({
                  id,
                  html,
                  printer,
                  print_settings: "{}",
                  margin: { top: 0, right: 0, bottom: 0, left: 0, unit: "mm" },
                  page_width: 80,
                  page_height: null,
                  orientation: "portrait",
                  copies: 1,
                  quality: 100,
                  grayscale: true,
                  remove_after_print: false,
                })
              : Promise.resolve();

          await Promise.all([
            job(`test-${Date.now()}-1`, p1),
            job(`test-${Date.now()}-2`, p2),
          ]);

          console.log("Test print sent to:", { p1, p2 });
        };

        if (mounted) {
          console.log("Ready: استخدم listPrinters(), savePrinters(p1,p2), testPrint()");
        }
      } catch (e) {
        console.warn("Failed to init printer helpers:", e);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // ✅ راقب حالة المصادقة: إذا صار المستخدم null انقل إلى login
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) navigate("/login", { replace: true });
    });
    return () => unsub();
  }, [navigate]);

  // ✅ تحديث role عند فتح الصفحة
  useEffect(() => {
    setRole(Number(localStorage.getItem("role") || "2"));
  }, []);

  async function handleLogout() {
    try {
      await signOut(auth);
    } catch (e) {
      console.warn("signOut failed:", e);
    }
    try {
      localStorage.clear();
      if (typeof sessionStorage !== "undefined") sessionStorage.clear();
    } catch {}
    window.location.replace("/login");
  }

  const cards = useMemo(() => {
    return isAdmin
      ? [
          { title: "المستخدمون", path: "/users_admin" },
          { title: "تقارير المنتجات", path: "/item_reports" },
          { title: "إضافة منتج", path: "/add_item" },
          { title: "إنشاء حساب", path: "/create_user" },
          { title: "إضافة قسم", path: "/add_category" },
        ]
      : BASE_CARDS;
  }, [isAdmin]);

  const Header = () => (
    <header
      className="relative w-full rounded-3xl px-6 py-12 text-center overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
        boxShadow: "0 18px 50px rgba(0,0,0,0.10)",
      }}
    >
      <HeaderSketch />

      {/* زر تسجيل الخروج */}
      <button
        onClick={handleLogout}
        className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
        style={{
          background: "rgba(255,255,255,0.10)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: BRAND.white,
        }}
        title="تسجيل الخروج"
      >
        <ArrowLeftOnRectangleIcon className="h-5 w-5" />
        <span
          className="text-sm font-extrabold"
          style={{ fontFamily: "Tajawal, system-ui, sans-serif" }}
        >
          تسجيل الخروج
        </span>
      </button>

      <div className="relative">
        <Typography
          variant="h2"
          className="font-bold text-white tracking-wide mb-2"
          style={{ fontFamily: "Tajawal, system-ui, sans-serif" }}
        >
          لوحة التحكم
        </Typography>

        <Typography
          className="text-white/90"
          style={{ fontFamily: "Tajawal, system-ui, sans-serif" }}
        >
          اختر صفحة للانتقال إليها
        </Typography>

        <div className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-1"
          style={{
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: BRAND.white,
            fontFamily: "Tajawal, system-ui, sans-serif",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          <span>الصلاحية:</span>
          <span>{isAdmin ? "أدمن" : "موظف"}</span>
        </div>
      </div>
    </header>
  );

  const Tile = ({ title, path }) => {
    const Icon = ICONS[path] || Squares2X2Icon;

    return (
      <button
        onClick={() => navigate(path)}
        aria-label={`اذهب إلى ${title}`}
        className="group text-right"
      >
        <Card
          className="rounded-3xl transition-all duration-200 hover:-translate-y-0.5"
          style={{
            background: BRAND.white,
            border: `1px solid ${BRAND.paper}`,
            boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          }}
        >
          <CardBody className="flex items-center justify-between p-5">
            <div className="flex-1">
              <Typography
                variant="h6"
                className="mb-1"
                style={{
                  color: BRAND.ink,
                  fontFamily: "Tajawal, system-ui, sans-serif",
                  fontWeight: 900,
                }}
              >
                {title}
              </Typography>
              <div
                className="text-xs"
                style={{
                  color: BRAND.graphite,
                  fontFamily: "Tajawal, system-ui, sans-serif",
                }}
              >
                فتح الصفحة وإدارة البيانات
              </div>
            </div>

            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl transition-all group-hover:scale-105"
              style={{
                background: `linear-gradient(180deg, ${BRAND.paper}, ${BRAND.white})`,
                border: `1px dashed ${BRAND.mist}`,
              }}
            >
              <Icon className="h-6 w-6" style={{ color: BRAND.ink }} />
            </div>
          </CardBody>

          <div
            className="h-1 w-full rounded-b-3xl"
            style={{ background: BRAND.mist }}
          />
        </Card>
      </button>
    );
  };

  return (
    <div
      dir="rtl"
      className="min-h-screen py-8 px-4"
      style={{
        background: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
      }}
    >
      <div className="mx-auto max-w-6xl space-y-8">
        <Header />

        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => (
            <Tile key={c.path} {...c} />
          ))}
        </section>

        <footer className="pt-4 text-center">
          <span
            className="inline-block rounded-full px-4 py-2 text-sm"
            style={{
              backgroundColor: BRAND.white,
              border: `1px solid ${BRAND.paper}`,
              color: BRAND.graphite,
              fontFamily: "Tajawal, system-ui, sans-serif",
              boxShadow: "0 10px 24px rgba(0,0,0,0.04)",
            }}
          >
            © 2025 Alayadi Digital Solutions
          </span>
        </footer>
      </div>
    </div>
  );
}
